import React, { useEffect, useRef } from 'react';

export const matchTabId = (baseId: string, tab: string): string => `${baseId}-tab-${tab}`;
export const matchPanelId = (baseId: string, tab: string): string => `${baseId}-panel-${tab}`;

const MatchTabs = <T extends string>({ baseId, tabs, active, onChange, ariaLabel = 'Match sections' }: { baseId: string; tabs: readonly { id: T; label: string }[]; active: T; onChange: (tab: T) => void; ariaLabel?: string }) => {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const previousActive = useRef(active);
  useEffect(() => {
    if (previousActive.current !== active) refs.current[tabs.findIndex(tab => tab.id === active)]?.focus();
    previousActive.current = active;
  }, [active, tabs]);
  const select = (index: number) => { const tab = tabs[index]; if (!tab) return; onChange(tab.id); window.requestAnimationFrame(() => refs.current[index]?.focus()); };
  return <div role="tablist" aria-label={ariaLabel} className="flex max-w-full gap-1 overflow-x-auto pb-1">{tabs.map((tab, index) => <button
    key={tab.id}
    ref={element => { refs.current[index] = element; }}
    id={matchTabId(baseId, tab.id)}
    type="button"
    role="tab"
    tabIndex={active === tab.id ? 0 : -1}
    aria-selected={active === tab.id}
    aria-controls={matchPanelId(baseId, tab.id)}
    onClick={() => onChange(tab.id)}
    onKeyDown={event => {
      let next: number | undefined;
      if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      if (next !== undefined) { event.preventDefault(); select(next); }
    }}
    className={`min-h-11 shrink-0 rounded-md px-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 ${active === tab.id ? 'bg-club-blue-700 text-white' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800'}`}
  >{tab.label}</button>)}</div>;
};
export default MatchTabs;
